import React from 'react';

interface LogoProps {
  variant: 'current' | 'monogram-1' | 'monogram-2' | 'circle-1' | 'circle-2' | 'wordmark-1' | 'wordmark-2' | 'shield-1' | 'shield-2' | 'geometric-1' | 'geometric-2' | 'infinity-1' | 'infinity-2' | 'compass-1' | 'compass-2';
  className?: string;
  textColor?: string;
  accentColor?: string;
}

export default function LogoVariations({ variant, className = '', textColor = '#2D2A26', accentColor = '#C8A97E' }: LogoProps) {
  
  // Current Logo
  if (variant === 'current') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: accentColor }}>
          <span className="text-white font-serif text-lg leading-none">F</span>
        </div>
        <span className="text-xl font-serif tracking-wide" style={{ color: textColor }}>
          Full Circle <span style={{ color: accentColor }}>Assist</span>
        </span>
      </div>
    );
  }

  // Monogram 1 - Interlocking FC
  if (variant === 'monogram-1') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect width="36" height="36" rx="6" fill={accentColor} fillOpacity="0.1"/>
          <path d="M12 10H22V13H15V17H21V20H15V26H12V10Z" fill={accentColor}/>
          <path d="M20 16C20 13.7909 21.7909 12 24 12C25.0609 12 26.0783 12.4214 26.8284 13.1716L24.7071 15.2929C24.5196 15.1054 24.2652 15 24 15C23.4477 15 23 15.4477 23 16V20C23 20.5523 23.4477 21 24 21C24.2652 21 24.5196 20.8946 24.7071 20.7071L26.8284 22.8284C26.0783 23.5786 25.0609 24 24 24C21.7909 24 20 22.2091 20 20V16Z" fill={accentColor}/>
        </svg>
        <span className="text-xl font-serif tracking-wide" style={{ color: textColor }}>
          Full Circle <span style={{ color: accentColor }}>Assist</span>
        </span>
      </div>
    );
  }

  // Monogram 2 - Stacked FCA
  if (variant === 'monogram-2') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect width="36" height="36" rx="18" fill={accentColor}/>
          <text x="18" y="14" textAnchor="middle" fill="white" fontSize="8" fontFamily="serif" fontWeight="600">F</text>
          <text x="18" y="21" textAnchor="middle" fill="white" fontSize="8" fontFamily="serif" fontWeight="600">C</text>
          <text x="18" y="28" textAnchor="middle" fill="white" fontSize="8" fontFamily="serif" fontWeight="600">A</text>
        </svg>
        <span className="text-xl font-serif tracking-wide" style={{ color: textColor }}>
          Full Circle <span style={{ color: accentColor }}>Assist</span>
        </span>
      </div>
    );
  }

  // Circle 1 - Broken circle with arrow
  if (variant === 'circle-1') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M18 4C10.268 4 4 10.268 4 18C4 25.732 10.268 32 18 32C25.732 32 32 25.732 32 18" stroke={accentColor} strokeWidth="2.5" strokeLinecap="round"/>
          <path d="M32 18L28 14M32 18L28 22M32 18H26" stroke={accentColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span className="text-xl font-serif tracking-wide" style={{ color: textColor }}>
          Full Circle <span style={{ color: accentColor }}>Assist</span>
        </span>
      </div>
    );
  }

  // Circle 2 - Concentric circles
  if (variant === 'circle-2') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="18" cy="18" r="14" stroke={accentColor} strokeWidth="2"/>
          <circle cx="18" cy="18" r="9" stroke={accentColor} strokeWidth="2"/>
          <circle cx="18" cy="18" r="4" fill={accentColor}/>
        </svg>
        <span className="text-xl font-serif tracking-wide" style={{ color: textColor }}>
          Full Circle <span style={{ color: accentColor }}>Assist</span>
        </span>
      </div>
    );
  }

  // Wordmark 1 - Elegant serif only
  if (variant === 'wordmark-1') {
    return (
      <div className={`${className}`}>
        <div className="text-2xl font-serif tracking-wider" style={{ color: textColor }}>
          FULL CIRCLE
        </div>
        <div className="text-sm tracking-[0.3em] mt-0.5" style={{ color: accentColor }}>
          ASSIST
        </div>
      </div>
    );
  }

  // Wordmark 2 - Modern minimal
  if (variant === 'wordmark-2') {
    return (
      <div className={`${className}`}>
        <span className="text-2xl font-serif tracking-wide" style={{ color: textColor }}>
          Full Circle <span className="font-light" style={{ color: accentColor }}>Assist</span>
        </span>
      </div>
    );
  }

  // Shield 1 - Classic crest
  if (variant === 'shield-1') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <svg width="36" height="40" viewBox="0 0 36 40" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M18 2L4 8V18C4 27 10 34 18 38C26 34 32 27 32 18V8L18 2Z" fill={accentColor} fillOpacity="0.1" stroke={accentColor} strokeWidth="2"/>
          <text x="18" y="24" textAnchor="middle" fill={accentColor} fontSize="14" fontFamily="serif" fontWeight="600">F</text>
        </svg>
        <span className="text-xl font-serif tracking-wide" style={{ color: textColor }}>
          Full Circle <span style={{ color: accentColor }}>Assist</span>
        </span>
      </div>
    );
  }

  // Shield 2 - Modern badge
  if (variant === 'shield-2') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <svg width="36" height="40" viewBox="0 0 36 40" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M18 2L4 8V18C4 27 10 34 18 38C26 34 32 27 32 18V8L18 2Z" fill={accentColor}/>
          <path d="M12 16H20V18H14V20H19V22H14V26H12V16Z" fill="white"/>
          <circle cx="24" cy="21" r="3" stroke="white" strokeWidth="1.5"/>
        </svg>
        <span className="text-xl font-serif tracking-wide" style={{ color: textColor }}>
          Full Circle <span style={{ color: accentColor }}>Assist</span>
        </span>
      </div>
    );
  }

  // Geometric 1 - Overlapping circles
  if (variant === 'geometric-1') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <svg width="40" height="36" viewBox="0 0 40 36" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="14" cy="18" r="12" stroke={accentColor} strokeWidth="2" fill="none"/>
          <circle cx="26" cy="18" r="12" stroke={accentColor} strokeWidth="2" fill="none"/>
          <circle cx="20" cy="18" r="4" fill={accentColor}/>
        </svg>
        <span className="text-xl font-serif tracking-wide" style={{ color: textColor }}>
          Full Circle <span style={{ color: accentColor }}>Assist</span>
        </span>
      </div>
    );
  }

  // Geometric 2 - Hexagonal pattern
  if (variant === 'geometric-2') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M18 2L32 10V26L18 34L4 26V10L18 2Z" stroke={accentColor} strokeWidth="2" fill="none"/>
          <path d="M18 10L26 14.5V23.5L18 28L10 23.5V14.5L18 10Z" fill={accentColor} fillOpacity="0.2"/>
          <circle cx="18" cy="18" r="3" fill={accentColor}/>
        </svg>
        <span className="text-xl font-serif tracking-wide" style={{ color: textColor }}>
          Full Circle <span style={{ color: accentColor }}>Assist</span>
        </span>
      </div>
    );
  }

  // Infinity 1 - Classic infinity
  if (variant === 'infinity-1') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <svg width="44" height="28" viewBox="0 0 44 28" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M11 4C5.477 4 1 8.477 1 14C1 19.523 5.477 24 11 24C14.5 24 17.5 22 19.5 19.5L24.5 14L19.5 8.5C17.5 6 14.5 4 11 4Z" stroke={accentColor} strokeWidth="2" fill="none"/>
          <path d="M33 4C38.523 4 43 8.477 43 14C43 19.523 38.523 24 33 24C29.5 24 26.5 22 24.5 19.5L19.5 14L24.5 8.5C26.5 6 29.5 4 33 4Z" stroke={accentColor} strokeWidth="2" fill="none"/>
        </svg>
        <span className="text-xl font-serif tracking-wide" style={{ color: textColor }}>
          Full Circle <span style={{ color: accentColor }}>Assist</span>
        </span>
      </div>
    );
  }

  // Infinity 2 - Modern loop
  if (variant === 'infinity-2') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <svg width="40" height="32" viewBox="0 0 40 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M2 16C2 10.477 6.477 6 12 6C15.5 6 18.5 8 20 10.5C21.5 8 24.5 6 28 6C33.523 6 38 10.477 38 16C38 21.523 33.523 26 28 26C24.5 26 21.5 24 20 21.5C18.5 24 15.5 26 12 26C6.477 26 2 21.523 2 16Z" fill={accentColor} fillOpacity="0.15" stroke={accentColor} strokeWidth="2"/>
        </svg>
        <span className="text-xl font-serif tracking-wide" style={{ color: textColor }}>
          Full Circle <span style={{ color: accentColor }}>Assist</span>
        </span>
      </div>
    );
  }

  // Compass 1 - Traditional compass rose
  if (variant === 'compass-1') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="18" cy="18" r="14" stroke={accentColor} strokeWidth="1.5"/>
          <path d="M18 4V10M18 26V32M4 18H10M26 18H32" stroke={accentColor} strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M18 8L20 16L18 18L16 16L18 8Z" fill={accentColor}/>
          <path d="M18 28L16 20L18 18L20 20L18 28Z" fill={accentColor} fillOpacity="0.4"/>
          <circle cx="18" cy="18" r="2" fill={accentColor}/>
        </svg>
        <span className="text-xl font-serif tracking-wide" style={{ color: textColor }}>
          Full Circle <span style={{ color: accentColor }}>Assist</span>
        </span>
      </div>
    );
  }

  // Compass 2 - Modern directional
  if (variant === 'compass-2') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="18" cy="18" r="14" stroke={accentColor} strokeWidth="2" fill="none"/>
          <path d="M18 6L22 16L18 18L14 16L18 6Z" fill={accentColor}/>
          <path d="M18 30L14 20L18 18L22 20L18 30Z" fill={accentColor} fillOpacity="0.3"/>
          <path d="M6 18L16 14L18 18L16 22L6 18Z" fill={accentColor} fillOpacity="0.5"/>
          <path d="M30 18L20 22L18 18L20 14L30 18Z" fill={accentColor} fillOpacity="0.7"/>
        </svg>
        <span className="text-xl font-serif tracking-wide" style={{ color: textColor }}>
          Full Circle <span style={{ color: accentColor }}>Assist</span>
        </span>
      </div>
    );
  }

  return null;
}
